/* eslint-disable no-unused-vars */
import {useState, useLayoutEffect, useEffect} from 'react';
import {useForm, Controller} from 'react-hook-form';
import {useLocation, useNavigate, Link} from 'react-router-dom';
import {useIntl, FormattedMessage} from 'react-intl';

import api from '../../../../services/api';
import {useQuery} from '../../../../hooks/useQuery';
import {useAuth} from '../../hooks/useAuth';
import {useTranslation} from '../../../translation/hooks/useTranslation';

import Input from '../../../../components/_form/Input';
import InputPassword from '../../../../components/_form/InputPassword';
import MaskedInput from '../../../../components/_form/MaskedInput';
import Button from '../../../../components/Button';
import {LoadingBox} from '../../../../components/LoadingBox';

import logo from '../../../../assets/images/logos-inline-color.png';
import worker1 from '../../../../assets/images/worker1.jpeg';
import brFlag from '../../../../assets/images/flags/brazil-rd.png';
import usaFlag from '../../../../assets/images/flags/usa-rd.png';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {formatMessage, locale} = useIntl();
  const {onChangeLang, lang} = useTranslation();
  const queryParams = useQuery(location.search);
  const comeFromLP = queryParams.get('lp');

  const {signUp} = useAuth();
  const {
    control,
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: {errors},
  } = useForm({
    defaultValues: {
      name: queryParams.get('name'),
      email: queryParams.get('email'),
    },
  });

  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState({
    loading: true,
    data: {},
  });
  const [seePassword, setSeePassword] = useState(false);

  const canUseEmail = async (email) => {
    try {
      if (email.length < 5) return false;

      const {data} = await api.post('/validations', {
        type: 'full_email',
        value: email.toLowerCase(),
      });

      if (data.hasValue) return false;

      return true;
    } catch (_) {
      return false;
    }
  };

  const canUseUsername = async (value) => {
    try {
      if (value.length < 5) return false;

      const {data} = await api.post('/validations', {
        type: 'username',
        value,
      });

      if (data.hasValue) return false;

      return true;
    } catch (_) {
      return false;
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const utm_source =
        queryParams.get('utm_source') || localStorage.getItem('utm_source');
      const utm_campaign =
        queryParams.get('utm_campaign') || localStorage.getItem('utm_campaign');
      const utm_id =
        queryParams.get('utm_id') || localStorage.getItem('utm_id');
      const utm_content =
        queryParams.get('utm_content') || localStorage.getItem('utm_content');

      const payload = {
        ...data,
        username: data.username.toLowerCase().replace(/\s+/g, ''),
        password: data.password.replace(/\s+/g, ''),
        email: data.email.toLowerCase().replace(/\s+/g, ''),
        status: 'active',
        role: 'customer',
        plan_id: plan.data._id,
        plan_stripe_id: plan.data.stripe_id,
        sale_id: queryParams.get('sid') ? queryParams.get('sid') : '',
        utm_source: utm_source || 'organic',
        utm_campaign: utm_campaign || 'organic',
        utm_id: utm_id || 'organic',
        utm_content: utm_content || 'organic',
        trial: true,
        language: locale || 'en',
      };
      await api.post('/subscription', payload);
      window.dataLayer.push({
        event: 'new-account',
        plan: plan.data.name,
        locale,
        company_name: data.company_name,
      });
      await signUp(payload);
    } catch (error) {
      window.dataLayer.push({
        event: 'new-account-error',
        plan: error.response.data || 'n/i',
      });
      if (
        error.response.data &&
        error.response.data.err &&
        error.response.data.err.errors
      ) {
        const errorsKeys = Object.keys(error.response.data.err.errors);
        errorsKeys.forEach((k) => {
          const errorObj = error.response.data.err.errors[k];
          const field = errorObj.path;
          const message = errorObj.message.replace('Path', 'Field');
          setError(field, {
            type: 'manual',
            message,
          });
        });
      }

      if (
        error.response.data &&
        error.response.data.err &&
        error.response.data.err.data &&
        error.response.data.err.data.param
      ) {
        let field = error.response.data.err.data.param;

        if (
          error.response.data.err.data.param === 'exp_year' ||
          error.response.data.err.dataparam === 'exp_month'
        ) {
          field = 'expiry';
        }
        if (error.response.data.err.data.param === 'number') {
          field = 'card_number';
        }

        setError(field, {
          type: 'manual',
          message: error.response.data.err.data.msg || 'invalid data',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    setValue(event.target.name, event.target.value.trim());
  };

  useLayoutEffect(() => {
    async function fetchPlan() {
      try {
        let planParam = queryParams.get('p');
        if (
          !planParam ||
          !['workpodium', 'workpodium-yearly'].includes(planParam)
        ) {
          planParam = 'workpodium';
        }
        const {data} = await api.get(`/plans/${planParam}`);
        setPlan({loading: false, data});
      } catch (error) {
        navigate('/new-account?p=workpodium');
      }
    }

    fetchPlan();
  }, []);

  useEffect(() => {
    const utm_source = queryParams.get('utm_source');
    const utm_campaign = queryParams.get('utm_campaign');
    const utm_id = queryParams.get('utm_id');
    const utm_content = queryParams.get('utm_content');

    if (utm_source) localStorage.setItem('utm_source', utm_source);
    if (utm_campaign) localStorage.setItem('utm_campaign', utm_campaign);
    if (utm_id) localStorage.setItem('utm_id', utm_id);
    if (utm_content) localStorage.setItem('utm_content', utm_content);
  }, []);

  return (
    <>
      <div className="relative bg-white lg:h-screen lg:overflow-hidden">
        <div className="fixed right-0 top-0 flex flex-col items-center justify-center gap-2 p-2 bg-yellow-500 z-50 md:right-auto md:left-0">
          <p className="text-xs text-center font-light lg:text-md">
            {formatMessage({id: 'signup.choose-country'})}
          </p>
          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => onChangeLang('en')}
              className={classNames(
                lang === 'en' ? 'border-white' : 'border-transparent',
                'flex items-center border-2 rounded-full',
              )}>
              <img src={usaFlag} alt="United States flag" width={34} />
            </button>

            <button
              type="button"
              onClick={() => onChangeLang('pt-BR')}
              className={classNames(
                lang === 'pt-BR' ? 'border-white' : 'border-transparent',
                'flex items-center border-2 rounded-full',
              )}>
              <img src={brFlag} alt="Brazil flag" width={34} />
            </button>
          </div>
        </div>

        <div className="lg:h-full w-full lg:w-7/12">
          <div className="flex flex-col items-center justify-center w-full h-full p-4 pb-44 border-t-4 border-yellow-500 md:p-8 md:border-0">
            <div className="flex justify-start w-full mt-2 mb-6 lg:mt-0 lg:mb-0">
              <img
                className="h-8 w-auto mb-2 lg:mx-auto lg:h-12 lg:mb-4"
                src={logo}
                alt="WorkPodium - Logo"
              />
            </div>
            {comeFromLP ? (
              <h1 className="text-2xl font-extralight text-center lg:text-4xl">
                Complete your{' '}
                <span className="font-bold text-yellow-500">registration</span>{' '}
                and <br />
                see the difference <span className="font-bold">
                  WorkPodium
                </span>{' '}
                makes.
              </h1>
            ) : (
              <>
                <h1 className="text-xl font-extralight text-center lg:text-4xl">
                  <FormattedMessage
                    id="signup.title"
                    values={{
                      span: (chunks) => (
                        <span className="font-bold">{chunks}</span>
                      ),
                    }}
                  />
                  <br />
                  <span className="hidden lg:block">
                    <FormattedMessage
                      id="signup.title.two"
                      values={{
                        span: (chunks) => (
                          <span className="font-bold">{chunks}</span>
                        ),
                      }}
                    />
                  </span>
                </h1>
                <p className="block text-xs text-center font-light lg:text-md md:hidden">
                  {formatMessage({id: 'signup.no-creditcard'})}
                  <br />
                  <span className="italic font-semibold text-yellow-600">
                    {formatMessage({id: 'signup.cancel'})}
                  </span>
                </p>
              </>
            )}

            <form
              className="w-full max-w-xl px-4 md:px-10 mt-6"
              onSubmit={handleSubmit(onSubmit)}>
              <div className="grid gap-4 mt-2 grid-cols-1 lg:grid-cols-2">
                <Input
                  label={`${formatMessage({id: 'your_name'})}*`}
                  id="name"
                  error={errors.name}
                  errorMessage={errors.name?.message}
                  autoFocus={!comeFromLP}
                  {...register('name', {
                    required: true,
                  })}
                />

                <Input
                  label={`${formatMessage({id: 'company_name'})}*`}
                  id="company_name"
                  error={errors.company_name}
                  errorMessage={errors.company_name?.message}
                  autoFocus={!!comeFromLP}
                  {...register('company_name', {required: true})}
                />
              </div>

              <div className="mt-2 grid gap-4 grid-cols-1 lg:grid-cols-2">
                <Input
                  label="Email *"
                  id="email"
                  error={errors.email}
                  errorMessage={errors.email && errors.email.message}
                  {...register('email', {
                    required: true,
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                      message: formatMessage({id: 'validation.email.invalid'}),
                    },
                    validate: {
                      checkUrl: async (value) =>
                        (await canUseEmail(value)) ||
                        formatMessage({id: 'validation.email.user'}),
                    },
                  })}
                  onChange={handleChange}
                />

                <Controller
                  rules={{
                    required: true,
                    minLength: 14,
                    pattern: {
                      value: /^[^_]*$/i,
                      message: formatMessage({id: 'validation.phone.valid'}),
                    },
                  }}
                  name="phone"
                  control={control}
                  render={({field}) => (
                    <MaskedInput
                      {...field}
                      label={`${formatMessage({id: 'phone'})}*`}
                      mask="(999) 999-9999"
                      maskPlaceholder="(999) 999-9999"
                      alwaysShowMask={false}
                      removeMask={false}
                      error={errors.phone}
                      errorMessage={errors.phone ? errors.phone.message : ''}
                    />
                  )}
                />
              </div>

              <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-2">
                <Input
                  label={`${formatMessage({id: 'username'})}*`}
                  id="username"
                  error={errors.username}
                  errorMessage={errors.username && errors.username.message}
                  inputExtraClassName="lowercase"
                  {...register('username', {
                    required: true,
                    minLength: {
                      value: 4,
                      message: formatMessage({
                        id: 'validation.username.length',
                      }),
                    },
                    validate: {
                      checkUrl: async (value) =>
                        (await canUseUsername(value)) ||
                        formatMessage({id: 'validation.username.used'}),
                    },
                  })}
                  onChange={handleChange}
                />

                <InputPassword
                  label={`${formatMessage({id: 'password'})}*`}
                  id="password"
                  error={errors.password}
                  errorMessage={errors.password && errors.password.message}
                  onShowPassword={() => setSeePassword((prev) => !prev)}
                  seePassword={seePassword}
                  {...register('password', {
                    required: true,
                    minLength: {
                      value: 4,
                      message: formatMessage({
                        id: 'validation.username.length',
                      }),
                    },
                  })}
                  onChange={handleChange}
                />
              </div>

              <div className="mt-6">
                <Button
                  loading={loading}
                  type="submit"
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500">
                  {formatMessage({id: 'signup.cta'})}
                </Button>

                <p className="text-xs text-center font-normal text-gray-500 mt-4">
                  <FormattedMessage
                    id="signup.terms"
                    values={{
                      t: (chunks) => (
                        <a
                          href="https://workpodium.com/privacy-policy/"
                          target="_blank"
                          className="font-bold"
                          rel="noreferrer">
                          {chunks}
                        </a>
                      ),
                      pp: (chunks) => (
                        <a
                          href="https://workpodium.com/privacy-policy/"
                          target="_blank"
                          className="font-bold"
                          rel="noreferrer">
                          {chunks}
                        </a>
                      ),
                    }}
                  />
                </p>

                <p className="hidden text-lg text-center font-light mt-4 md:block">
                  {formatMessage({id: 'signup.no-creditcard'})}
                  <br />
                  <span className="italic font-semibold text-yellow-600">
                    {formatMessage({id: 'signup.cancel'})}
                  </span>
                </p>
              </div>

              <div>
                <p className="mt-8 text-xs font-light text-center text-gray-400">
                  {' '}
                  {formatMessage({id: 'signup.have-account'})}{' '}
                  <Link
                    to="/"
                    className="font-medium text-yellow-500 hover:text-yellow-600">
                    {formatMessage({id: 'signup.have-account.cta'})}
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
        <div className="hidden lg:flex w-5/12 lg:absolute lg:inset-y-0 lg:right-0 bg-gradient-to-b from-yellow-500 to-yellow-700">
          <img
            className="h-full w-full object-cover lg:w-full lg:h-full opacity-10"
            src={worker1}
            alt="worker"
          />
        </div>
      </div>

      {loading && <LoadingBox info="Creating your account..." />}
    </>
  );
};

export default SignUp;
